import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Point, SankeyNodeObject } from 'highcharts';
import Card from '@components-clean/core/card';
import { IPopoutInfo, SankeyKeys } from '@components-clean/core/charts/sankey';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import PanelFrame from '@components-clean/core/panel-frame';
import { useLocalState } from '@hooks/index';
import { PanelType } from '@types/graphql-global-types';
import KriOverviewApi from '@apis/bff-api/central-monitoring/kri-overview';

interface IProps {
    studyRef: string;
    panelReference?: string;
    datasetId?: number;
    comparisonDatasetId?: number;
    popoutInfo?: IPopoutInfo;
    panelTitle?: string;
    includePurpleSites?: boolean;
}

interface ISankeyRow {
    siteId: number;
    from: number | null;
    to: number | null;
}

interface SankeyPoint extends Point {
    fromNode: SankeyNodeObject;
    toNode: SankeyNodeObject;
    weight: number;
}

interface ITooltipProps {
    context: Point | SankeyNodeObject;
    data: ISankeyRow[];
}

enum Colour {
    Red = 1,
    Amber = 2,
    Green = 3,
    Purple = 4,
}

export const Tooltip = ({ context, data }: ITooltipProps) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.kriOverview.cards.sankey.tooltip',
    });

    const isNode = Object.values(SankeyKeys).includes(context.name as SankeyKeys);

    const point = context as SankeyPoint;

    let content;

    if (isNode) {
        const colour = context.name;

        const totalPrevious = () => {
            switch (colour) {
                case SankeyKeys.RED:
                    return data.filter((d) => d.from === Colour.Red).length;
                case SankeyKeys.AMBER:
                    return data.filter((d) => d.from === Colour.Amber).length;
                case SankeyKeys.GREEN:
                    return data.filter((d) => d.from === Colour.Green).length;
                case SankeyKeys.PURPLE:
                    return data.filter((d) => d.from === Colour.Purple).length;
                case SankeyKeys.GRAY:
                    return data.filter((d) => d.from === null).length;
                default:
                    throw Error(`Invalid colour found: ${colour}`);
            }
        };

        const totalNoLonger = () => {
            let result: number = 0;
            switch (colour) {
                case SankeyKeys.RED:
                    result = data.filter((d) => d.from === Colour.Red && d.to !== Colour.Red).length;
                    break;
                case SankeyKeys.AMBER:
                    result = data.filter((d) => d.from === Colour.Amber && d.to !== Colour.Amber).length;
                    break;
                case SankeyKeys.GREEN:
                    result = data.filter((d) => d.from === Colour.Green && d.to !== Colour.Green).length;
                    break;
                case SankeyKeys.PURPLE:
                    result = data.filter((d) => d.from === Colour.Purple && d.to !== Colour.Purple).length;
                    break;
                case SankeyKeys.GRAY:
                    result = data.filter((d) => d.from === null && d.to !== null).length;
                    break;
                default:
                    throw Error(`Invalid colour found: ${colour}`);
            }
            return result * -1;
        };

        const totalUnchanged = () => {
            switch (colour) {
                case SankeyKeys.RED:
                    return data.filter((d) => d.from === Colour.Red && d.to === Colour.Red).length;
                case SankeyKeys.AMBER:
                    return data.filter((d) => d.from === Colour.Amber && d.to === Colour.Amber).length;
                case SankeyKeys.GREEN:
                    return data.filter((d) => d.from === Colour.Green && d.to === Colour.Green).length;
                case SankeyKeys.PURPLE:
                    return data.filter((d) => d.from === Colour.Purple && d.to === Colour.Purple).length;
                case SankeyKeys.GRAY:
                    return data.filter((d) => d.from === null && d.to === null).length;
                default:
                    throw Error(`Invalid colour found: ${colour}`);
            }
        };

        const totalBecame = () => {
            switch (colour) {
                case SankeyKeys.RED:
                    return data.filter((d) => d.from !== Colour.Red && d.to === Colour.Red).length;
                case SankeyKeys.AMBER:
                    return data.filter((d) => d.from !== Colour.Amber && d.to === Colour.Amber).length;
                case SankeyKeys.GREEN:
                    return data.filter((d) => d.from !== Colour.Green && d.to === Colour.Green).length;
                case SankeyKeys.PURPLE:
                    return data.filter((d) => d.from !== Colour.Purple && d.to === Colour.Purple).length;
                case SankeyKeys.GRAY:
                    return data.filter((d) => d.from !== null && d.to === null).length;
                default:
                    throw Error(`Invalid colour found: ${colour}`);
            }
        };

        const totalNow = () => {
            switch (colour) {
                case SankeyKeys.RED:
                    return data.filter((d) => d.to === Colour.Red).length;
                case SankeyKeys.AMBER:
                    return data.filter((d) => d.to === Colour.Amber).length;
                case SankeyKeys.GREEN:
                    return data.filter((d) => d.to === Colour.Green).length;
                case SankeyKeys.PURPLE:
                    return data.filter((d) => d.to === Colour.Purple).length;
                case SankeyKeys.GRAY:
                    return data.filter((d) => d.to === null).length;
                default:
                    throw Error(`Invalid colour found: ${colour}`);
            }
        };

        content = (
            <div>
                <dl>
                    <dt>
                        {t('node.previousTotal')} {colour}s
                    </dt>
                    <dd>
                        {/* Count of that colour from comparison dataset */}
                        {totalPrevious()}
                    </dd>

                    <dt>
                        {t('node.noLonger')} {colour}
                    </dt>
                    <dd>
                        {/* Number of sites that were {colour} but are no longer {colour} shown with a negative sign */}
                        {totalNoLonger()}
                    </dd>

                    <dt>{t('node.unchanged')}</dt>
                    <dd>
                        {/* Number of sites that were {colour} and still are {colour} */}
                        {totalUnchanged()}
                    </dd>

                    <dt>
                        {t('node.became')} {colour}
                    </dt>
                    <dd>
                        {/* Number of sites that have become  {colour} since the Comparison dataset */}
                        {totalBecame()}
                    </dd>

                    <dt>
                        {t('node.newTotal')} {colour}s
                    </dt>
                    <dd>
                        {/* Total {colour} in selected dataset */}
                        {totalNow()}
                    </dd>
                </dl>
            </div>
        );
    } else {
        content = (
            <>
                {point.fromNode.name} {'->'} {point.toNode.name}: {point.weight} {t('path.sites')}
            </>
        );
    }

    return <div className="tooltip">{content}</div>;
};

const SankeyContent = ({
    studyRef,
    datasetId,
    panelReference,
    comparisonDatasetId,
    popoutInfo,
    panelTitle,
    includePurpleSites,
}: IProps) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.kriOverview.cards.sankey',
    });
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;
    const { data: datasetScores, isLoading: isLoadingDatasetScores } = KriOverviewApi.useGetPanelSiteScores(
        studyRef,
        datasetId,
        panelReference,
    );
    const { data: comparisonScores, isLoading: isLoadingComparisonScores } = KriOverviewApi.useGetPanelSiteScores(
        studyRef,
        comparisonDatasetId,
        panelReference,
    );
    const [sankeyDefaultMessage, setSankeyDefaultMessage] = useState<string>(t('emptyMessage'));

    const sankeyData = useMemo(() => {
        if (!datasetScores || !comparisonScores) return [];

        const data: ISankeyRow[] = [];

        datasetScores.forEach((datasetSite) => {
            data.push({
                siteId: datasetSite.siteId,
                from: null,
                to: datasetSite.colour,
            });
        });

        comparisonScores.forEach((comparisonDatasetSite) => {
            let siteScore = data.find((site) => site.siteId === comparisonDatasetSite.siteId);
            if (!siteScore) {
                siteScore = {
                    siteId: comparisonDatasetSite.siteId,
                    to: null,
                    from: null,
                };
                data.push(siteScore);
            }

            siteScore.from = comparisonDatasetSite.colour;
        });

        return data;
    }, [datasetScores, comparisonScores]);

    // Update the default message displayed in the review sections based on dropdowns
    useEffect(() => {
        if (!comparisonDatasetId && !panelReference) {
            setSankeyDefaultMessage(t('noData'));
        }
        if (!comparisonDatasetId && panelReference) {
            setSankeyDefaultMessage(t('comparisonDatasetEmptyMessage'));
        }
        if (comparisonDatasetId && !panelReference) {
            setSankeyDefaultMessage(t('panelEmptyMessage'));
        }
        if (comparisonDatasetId && panelReference) {
            setSankeyDefaultMessage(t('noData'));
        }
    }, [panelReference, comparisonDatasetId]);

    return (
        <div className="review-rag-card">
            {isLoadingDatasetScores || isLoadingComparisonScores ? (
                <Card data-testid="sankey-chart-loading-card" style={{ height: '100%' }}>
                    <Loading />
                </Card>
            ) : sankeyData.length === 0 ? (
                <Card data-testid="sankey-chart-default-message-card" style={{ height: '100%' }}>
                    <InfoField message={sankeyDefaultMessage} type="Info" layout="center" />
                </Card>
            ) : (
                <div className="content">
                    <Card data-testid="sankey-chart-stats-card" title="Data">
                        <div className="overview">
                            <dl>
                                <dt>{t('overview.previousTotalRed')}</dt>
                                <dd>{sankeyData.filter((d) => d.from === Colour.Red).length}</dd>
                                <dt>{t('overview.totalRed')}</dt>
                                <dd>{sankeyData.filter((d) => d.to === Colour.Red).length}</dd>

                                <dt>{t('overview.previousTotalAmber')}</dt>
                                <dd>{sankeyData.filter((d) => d.from === Colour.Amber).length}</dd>
                                <dt>{t('overview.totalAmber')}</dt>
                                <dd>{sankeyData.filter((d) => d.to === Colour.Amber).length}</dd>

                                <dt>{t('overview.previousTotalGreen')}</dt>
                                <dd>{sankeyData.filter((d) => d.from === Colour.Green).length}</dd>
                                <dt>{t('overview.totalGreen')}</dt>
                                <dd>{sankeyData.filter((d) => d.to === Colour.Green).length}</dd>

                                {includePurpleSites && (
                                    <>
                                        <dt>{t('overview.previousTotalPurple')}</dt>
                                        <dd>{sankeyData.filter((d) => d.from === Colour.Purple).length}</dd>
                                        <dt>{t('overview.totalPurple')}</dt>
                                        <dd>{sankeyData.filter((d) => d.to === Colour.Purple).length}</dd>
                                    </>
                                )}
                            </dl>
                        </div>
                    </Card>
                    <PanelFrame
                        panelType={PanelType.GRAPH}
                        graphType="SANKEY"
                        panelData={{
                            title: `${studyName} ${panelTitle} RAG Change`,
                            data: {
                                green: {
                                    toGreen: sankeyData.filter((d) => d.from === Colour.Green && d.to === Colour.Green)
                                        .length,
                                    toAmber: sankeyData.filter((d) => d.from === Colour.Green && d.to === Colour.Amber)
                                        .length,
                                    toRed: sankeyData.filter((d) => d.from === Colour.Green && d.to === Colour.Red)
                                        .length,
                                    toPurple:
                                        sankeyData.filter((d) => d.from === Colour.Green && d.to === Colour.Purple)
                                            ?.length ?? undefined,
                                    toGray: sankeyData.filter((d) => d.from === Colour.Green && !d.to).length,
                                },
                                amber: {
                                    toGreen: sankeyData.filter((d) => d.from === Colour.Amber && d.to === Colour.Green)
                                        .length,
                                    toAmber: sankeyData.filter((d) => d.from === Colour.Amber && d.to === Colour.Amber)
                                        .length,
                                    toRed: sankeyData.filter((d) => d.from === Colour.Amber && d.to === Colour.Red)
                                        .length,
                                    toPurple:
                                        sankeyData.filter((d) => d.from === Colour.Amber && d.to === Colour.Purple)
                                            ?.length ?? undefined,
                                    toGray: sankeyData.filter((d) => d.from === Colour.Amber && !d.to).length,
                                },
                                red: {
                                    toGreen: sankeyData.filter((d) => d.from === Colour.Red && d.to === Colour.Green)
                                        .length,
                                    toAmber: sankeyData.filter((d) => d.from === Colour.Red && d.to === Colour.Amber)
                                        .length,
                                    toRed: sankeyData.filter((d) => d.from === Colour.Red && d.to === Colour.Red)
                                        .length,
                                    toPurple:
                                        sankeyData.filter((d) => d.from === Colour.Red && d.to === Colour.Purple)
                                            ?.length ?? undefined,
                                    toGray: sankeyData.filter((d) => d.from === Colour.Red && !d.to).length,
                                },
                                purple: {
                                    toGreen: sankeyData.filter((d) => d.from === Colour.Purple && d.to === Colour.Green)
                                        .length,
                                    toAmber: sankeyData.filter((d) => d.from === Colour.Purple && d.to === Colour.Amber)
                                        .length,
                                    toRed: sankeyData.filter((d) => d.from === Colour.Purple && d.to === Colour.Red)
                                        .length,
                                    toPurple:
                                        sankeyData.filter((d) => d.from === Colour.Purple && d.to === Colour.Purple)
                                            ?.length ?? undefined,
                                    toGray: sankeyData.filter((d) => d.from === Colour.Purple && !d.to).length,
                                },
                                gray: {
                                    toGreen: sankeyData.filter((d) => !d.from && d.to === Colour.Green).length,
                                    toAmber: sankeyData.filter((d) => !d.from && d.to === Colour.Amber).length,
                                    toRed: sankeyData.filter((d) => !d.from && d.to === Colour.Red).length,
                                    toPurple:
                                        sankeyData.filter((d) => !d.from && d.to === Colour.Purple)?.length ??
                                        undefined,
                                    toGray: sankeyData.filter((d) => !d.from && !d.to).length,
                                },
                            },
                        }}
                        title={t('title')}
                        data-testid="sankey-chart-card"
                        hidePanelSetup
                        TooltipElement={({ context }) => <Tooltip context={context} data={sankeyData} />}
                        popoutInfo={popoutInfo}
                        exportFilename={panelTitle}
                    />
                </div>
            )}
        </div>
    );
};

export default SankeyContent;
